import React from 'react';
import { track } from '@leagueplatform/analytics';
import {
  Card,
  HeadingText,
  queryHelpers,
  StackLayout,
  TextAction,
  useClickableCard,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import type { Achievement } from '@leagueplatform/rewards-api';
import { AchievementsDetailsModal } from '#components/achievements-details-modal/achievements-details-modal.tsx';
import { BadgeImage } from '../badge-image/badge-image.view';
import { EarnedTag } from '../earned-tag/earned-tag.view';

interface BadgeCardProps {
  achievement: Achievement;
  categoryTitle: string;
  cardIndex: number;
}

export const BadgeCard = ({
  achievement,
  categoryTitle,
  cardIndex,
}: BadgeCardProps) => {
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));
  const [showAchievementDetailsModal, setShowAchievementDetailsModal] =
    React.useState(false);
  const earnedBadgeRequirement = 2;
  const isAchievementDisabled = achievement.completions <= 0;
  const achievementStatus = isAchievementDisabled ? 'in_progress' : 'earned';

  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard();

  return (
    <>
      <Card.Elevated
        css={{
          position: 'relative',
          margin: isMobile ? '$half' : '$threeQuarters',
          height: isMobile ? '120px' : '136px',
          width: isMobile ? '100%' : '384px',
          maxWidth: isMobile ? '100%' : '384px',
        }}
      >
        <Card.ClickArea
          ref={clickAreaRef}
          onCardMouseUp={onCardMouseUp}
          onCardMouseDown={onCardMouseDown}
          css={{
            backgroundColor: '$surfaceBackgroundPrimary',
            padding: isMobile ? '$half' : '$threeQuarters',
          }}
        >
          <StackLayout
            orientation="horizontal"
            verticalAlignment="center"
            css={{
              gap: isMobile ? '$half' : '$threeQuarters',
            }}
          >
            <BadgeImage
              imageUrl={achievement.image?.large}
              isDisabled={isAchievementDisabled}
              css={{
                margin: isMobile ? '$threeQuarters' : '$oneAndHalf',
              }}
            />
            <HeadingText
              level="4"
              size="md"
              css={{
                color: isAchievementDisabled
                  ? '$onSurfaceTextSubdued'
                  : '$onSurfaceTextPrimary',
              }}
            >
              <TextAction
                as="button"
                ref={primaryActionRef}
                css={{
                  all: 'inherit',
                }}
                onClick={() => {
                  setShowAchievementDetailsModal(true);

                  track(
                    'rewards.button_clicked.about_me_achievements.navigate_to_achievement_item',
                    {
                      achievement_id: achievement.id,
                      sub_detail: achievement.name,
                      achievement_status: achievementStatus,
                      card_index: String(cardIndex + 1),
                      section_header: categoryTitle,
                    },
                  );
                }}
              >
                {achievement.name}
              </TextAction>
            </HeadingText>
            {achievement.completions >= earnedBadgeRequirement && (
              <EarnedTag completionAmount={achievement.completions} isBadge />
            )}
          </StackLayout>
        </Card.ClickArea>
      </Card.Elevated>
      <AchievementsDetailsModal
        open={showAchievementDetailsModal}
        onModalChange={() => {
          setShowAchievementDetailsModal(false);
        }}
        onCloseAutoFocus={() => {
          primaryActionRef.current?.focus();
        }}
        modalAchievement={achievement}
        closeModal={() => setShowAchievementDetailsModal(false)}
      />
    </>
  );
};
